import React, { useState } from 'react';
import Button from '../Button';
import { useEntitlement, callable, callableFailure, NOT_DEPLOYED } from '../../data/useEntitlement';
import { useDateFormat, useLocale } from '../../context/LocaleContext';
import { format } from '../../domain/money';
import { PLANS, STATES } from '../../domain/billingPlans';
import { hasLiveSubscription } from '../../domain/entitlement';
import { BRAND } from '../../domain/brand';
import { unverifiedMessage } from '../../domain/clientEvents';
import { metaInitiateCheckout, metaServerSignals } from '../../lib/metaPixel';
import s from './settings.module.css';

// Business Settings → Membership.
//
// ── What MMS has ───────────────────────────────────────────────────────────
// Three sections, in this order, and the structure below is theirs:
//
//   Subscription Status   the state ("Trial"), the plan and price on one line
//                         ("1 Teacher ($16.95 USD)"), and one action button
//                         ("Update Subscription")
//   Payment Method        empty: "You haven't added a payment method", with an
//                         "Add Payment Method" button
//   Billing History       empty: "You haven't been billed yet", and beneath it
//                         "Add your payment method above to subscribe to My
//                         Music Staff"
//
// ── Where we knowingly differ ──────────────────────────────────────────────
//
//  * One panel with three sections, not three cards: the section rule every
//    other multi-section panel in Business Settings uses (MessagingPanel).
//
//  * The price line says what period it is for — "1 Teacher ($6.99 USD a
//    month)" where MMS writes "1 Teacher ($16.95 USD)": one price, monthly,
//    for everybody (#722). The figure is the verdict's rather than a constant:
//    a comp is "no charge", and every other plan is what checkout sells. A
//    price held by hand before #722 is not quoted, since checkout never sold
//    it.
//
//  * Billing History does not list invoices. We do not mirror Stripe's
//    invoices into Firestore, and the client has no read path to Stripe, so a
//    studio that has been billed is sent to the Stripe portal, where the PDFs
//    live.
//
//  * No action button on a comped account: "Update Subscription" there would
//    open a checkout for a subscription nobody wants it to have.
//
// ── Nothing here commits a setting ─────────────────────────────────────────
//
// firestore.rules refuses every client write to both billing paths, so a
// browser console cannot award itself a comp. The buttons ask Cloud Functions
// for a Stripe session and hand the browser to the URL they return.

// What each state is called on screen. Prose lives here rather than in the
// domain, as the header of domain/entitlement.js asks: `state` and `reason` are
// stable strings for logs and switches.
const STATE_LABELS = {
  [STATES.trial]: 'Trial',
  [STATES.active]: 'Active',
  [STATES.grace]: 'Payment overdue',
  [STATES.granted]: 'Complimentary months',
  [STATES.comped]: 'Complimentary',
  // Not "Inactive": a studio with no billing account yet — the seconds after
  // signing up, before the account-stamping callable has answered — is one we
  // know nothing about. `unknown` is a state of its own that is entitled (see
  // ENTITLED_STATES in domain/billingPlans.js), so the label and the verdict
  // agree.
  [STATES.unknown]: 'Setting up',
  [STATES.lapsed]: 'Inactive',
};

// Why a lapsed studio is out. Split by `reason` rather than shown as one line,
// because "your trial ended" and "your card stopped working" are different
// news and only one of them is anybody's fault.
const LAPSED_LINES = {
  trial_ended: 'Your free trial has ended.',
  // Stripe still calls the subscription `active` and no renewal has been
  // collected for longer than the margin allows — a webhook we never received,
  // or a subscription that has quietly stopped billing. Not "your trial
  // ended", and not blaming their card, which has not failed.
  subscription_unpaid: 'We have not seen a payment for your latest billing period. Check your card in the billing portal.',
  subscription_cancelled: 'Your subscription was cancelled.',
  payment_failed_grace_over: 'We could not take payment, and the retries have run out.',
  comp_ended: 'Your complimentary access has ended.',
  awarded_months_used_up: 'The months added to your account have been used up.',
};

export default function MembershipPanel() {
  const {
    verdict, account, state, reason, ensureFailure, loading, today,
  } = useEntitlement();
  const formatDate = useDateFormat();
  // The studio's locale decides how the number is written; the billing
  // currency decides what it is written in — a studio in Seoul invoices its
  // families in won and pays us in dollars, and domain/locale.js answers only
  // the first. `verdict.currency` is BILLING_CURRENCY.
  const { locale } = useLocale();

  // Which button is mid-flight, and what went wrong with the last one. One of
  // each rather than a pair per button: only one of these can be in flight,
  // because a successful one navigates away from the page.
  const [busy, setBusy] = useState(null);
  const [failure, setFailure] = useState(null);

  const money = (minor) => format(minor, verdict.currency, locale);

  // Nothing until this studio's own record has been read: the verdict over an
  // unread account is a well-formed `unknown`, which would tell a paying teacher
  // their subscription was "Setting up" for as long as the snapshot took.
  // SettingsPage gates the other panels on `useSettings().loading`; this one
  // holds its own gate because it reads a different pair of documents. The
  // hooks above all run first, so this early return cannot change the hook
  // order.
  if (loading) {
    return (
      <div className={s.panel}>
        <div className={s.panelTitle}>Subscription Status</div>
        <p className={s.blurb}>Checking your subscription&hellip;</p>
      </div>
    );
  }

  // "Update Subscription" goes to the customer portal while Stripe is running a
  // subscription for this studio, and to checkout otherwise — the predicate the
  // server asks before it will open a checkout, so the button never leads to a
  // refusal. Not decided from `stripeCustomerId`: a studio that cancelled keeps
  // its customer, and the portal cannot start a new subscription; checkout
  // reuses the customer. Nor from the state: a studio in `grace` is past_due,
  // which is still a live subscription.
  const hasStripeCustomer = Boolean(account?.stripeCustomerId);
  const manageCallable = hasLiveSubscription(account) ? 'createPortalSession' : 'createCheckoutSession';

  // Ask a Cloud Function for a URL and go there.
  //
  // A deployment without the callables is reported as "not switched on yet"
  // rather than as an error: it is an expected state, and a red failure message
  // for one teaches a teacher to ignore red failure messages.
  const open = async (name) => {
    setBusy(name);
    setFailure(null);
    try {
      const fn = callable(name);
      // No Functions in this build at all — an unconfigured deployment. Same
      // branch as an undeployed function, and shaped as the same error so
      // there is one place that decides what to say.
      if (!fn) throw Object.assign(new Error('Functions unavailable'), { code: NOT_DEPLOYED });
      // Where Stripe should send the teacher back to. From the portal,
      // `window.location.href`, so they land back on the tab they left. From
      // Checkout, nothing: a completed checkout returns to the deployment's
      // STRIPE_CHECKOUT_SUCCESS_URL, which is the /subscribed thank-you page
      // (#754, functions/billing/README.md) — the address the paid conversion
      // is counted on, and a page with the way back to this tab.
      //
      // Opening Checkout is the Meta Pixel's InitiateCheckout (#780), sent
      // before the call so it has the length of the call to leave the page.
      // Its event id goes with the request as `metaEventId`, for the server
      // to send the same event under (Meta removes the duplicate); there is
      // none when the pixel sent nothing — declined, say — and then the field
      // is left out. The answer in force goes with it, and the pixel's
      // cookies beside a grant (`metaServerSignals`): the server records them
      // for the AddPaymentInfo and Purchase it sends from Stripe's webhook
      // (#781, functions/billing/metaCapi.js).
      let request = { returnUrl: window.location.href };
      if (name === 'createCheckoutSession') {
        const metaEventId = metaInitiateCheckout();
        request = { ...metaServerSignals(), ...(metaEventId ? { metaEventId } : {}) };
      }
      const res = await fn(request);
      const url = res?.data?.url;
      if (!url) throw new Error('No session URL returned');
      // Deliberately not left to a router: this is a Stripe-hosted page on
      // another origin, so it is a document navigation and not a route.
      window.location.assign(url);
      // `busy` is left set. The browser is on its way out of this document;
      // clearing it would make the button look ready again for the frame or
      // two before the page goes.
    } catch (err) {
      setFailure(callableFailure(err) || 'failed');
      setBusy(null);
    }
  };

  // ─── Subscription Status ─────────────────────────────────────────────────

  // Is this studio being charged anything? Asked of `state` first, not of
  // `priceMinor` alone: the sentence is about the state, and a panel depending
  // on the fold's arithmetic to say "free" is one arithmetic change away from
  // saying something else. Only a comp is free: an open comp is the plan while
  // it lasts, so the fold gives `priceMinor` 0, and a price held at zero by
  // hand from before #722 is not read (billingPlans.js `priceMinorFor`).
  const noCharge = state === STATES.comped || verdict.plan === PLANS.comp;

  // The plan and price on one line, MMS's shape: "1 Teacher ($16.95 USD)".
  // "1 Teacher" is literal: a studio is one teacher in this app.
  //
  // No price at all in `unknown`, the one state where the number would be made
  // up: a price on a page about an account we have not read is a claim about
  // that account.
  const planLine = state === STATES.unknown
    ? '1 Teacher'
    : (noCharge
      ? '1 Teacher (no charge)'
      : `1 Teacher (${money(verdict.priceMinor)} ${verdict.currency} a month)`);

  // What the state means, in a sentence. Every date here comes from the
  // verdict; nothing in this file does date arithmetic of its own.
  let statusLine = null;
  if (state === STATES.trial) {
    statusLine = verdict.daysLeft === 0
      ? `Your free trial ends today, ${formatDate(verdict.accessUntil)}.`
      : `Your free trial runs to ${formatDate(verdict.accessUntil)} — ${verdict.daysLeft} `
        + `${verdict.daysLeft === 1 ? 'day' : 'days'} left.`;
  } else if (state === STATES.active && verdict.trialEndsOn && today <= verdict.trialEndsOn) {
    // A card added during the trial (#713). Checkout starts the subscription
    // on a trial that ends with this one, and its $0 first invoice writes a
    // `paidThroughOn` — so "Paid through" here would claim a payment nobody
    // has made. What is true is when the first one can be.
    statusLine = `Your free trial runs to ${formatDate(verdict.trialEndsOn)}. `
      + 'Your card will not be charged before then.';
  } else if (state === STATES.active) {
    statusLine = verdict.paidThroughOn
      ? `Paid through ${formatDate(verdict.paidThroughOn)}.`
      : 'Your subscription is up to date.';
  } else if (state === STATES.grace && reason === 'renewal_pending') {
    // `grace` covers two situations that are not the same news, and this is the
    // one where nothing has gone wrong. `paidThroughOn` moves only on evidence
    // of payment (functions/billing/webhook.js), so between a period ending and
    // Stripe collecting the renewal — an hour, usually — a perfectly healthy
    // subscription sits here. Telling that studio we could not take their
    // payment would be a false alarm about their own card.
    statusLine = 'Your subscription is renewing. This page will update once the payment goes through.';
  } else if (state === STATES.grace) {
    statusLine = verdict.accessUntil
      ? `We could not take your last payment. We will keep trying your card, and your `
        + `studio stays open until ${formatDate(verdict.accessUntil)}.`
      : 'We could not take your last payment. We will keep trying your card.';
  } else if (state === STATES.granted) {
    statusLine = `${verdict.monthsAwarded} `
      + `${verdict.monthsAwarded === 1 ? 'month has' : 'months have'} been added to your `
      + `account. They run to ${formatDate(verdict.accessUntil)}.`;
  } else if (state === STATES.comped) {
    // A comp has no end until somebody writes one, which is why `accessUntil`
    // is null here rather than a far-future date (domain/entitlement.js).
    statusLine = `Your studio has been given ${BRAND.name} at no charge, with no end date.`;
  } else if (state === STATES.unknown) {
    // Nothing has been written down about this studio's billing yet: the gap
    // between signing up and the callable that stamps the account. Not a
    // lapse.
    statusLine = ensureFailure === 'not-deployed'
      ? 'Subscriptions are not switched on for this deployment yet.'
      : 'We are still setting up your account. This page will fill in on its own.';
  } else {
    statusLine = LAPSED_LINES[reason] || 'Your studio does not have an active subscription.';
  }

  // No introductory-price lines (#722): there is one price, for everybody, so
  // there is nothing to count down to. An account carrying `plan: 'intro'` and
  // an intro count shows the same.

  // No action while there is no account document to act on: the callables need
  // one. A comped studio gets none either — see the header.
  const canManage = state !== STATES.comped && state !== STATES.unknown;

  const failureNote = failure === 'not-deployed'
    ? 'Subscriptions are not switched on yet. This will start working as soon as the '
      + 'payment service is deployed — nothing has been charged.'
    : failure === 'unverified'
      ? unverifiedMessage()
      : failure
      ? 'We could not open the payment page. Please try again in a moment.'
      : null;

  // Whether this studio has ever been charged. `paidThroughOn` is stamped by a
  // successful payment, and `introInvoicesUsed` by a founder-rate invoice
  // before #722; either means the history is not empty. It cannot be listed
  // (see the header), so this only decides which of two things to say.
  const hasBeenBilled = Boolean(account?.paidThroughOn)
    || Number(account?.introInvoicesUsed) > 0;

  return (
    <div className={s.panel}>
      <div className={s.panelTitle}>Subscription Status</div>

      <div className={s.statusRow}>
        <span className={s.statusPill}>
          {/* `grace` is two situations under one state. A renewal in flight is
              not a payment problem, so it must not wear the label that says one
              — see the status line below. */}
          {(state === STATES.grace && reason === 'renewal_pending')
            ? 'Renewing'
            : (STATE_LABELS[state] || 'Inactive')}
        </span>
        <span className={s.planLine}>{planLine}</span>
      </div>

      <p className={s.note}>{statusLine}</p>

      {canManage ? (
        <div className={s.actions}>
          <Button
            variant="primary"
            loading={busy === manageCallable}
            disabled={Boolean(busy)}
            onClick={() => open(manageCallable)}
          >
            Update Subscription
          </Button>
        </div>
      ) : null}

      {failureNote ? <p className={s.failureNote}>{failureNote}</p> : null}

      {/* ── Payment Method ────────────────────────────────────────────── */}
      <div className={s.section}>
        <div className={s.panelTitle}>Payment Method</div>
        {hasStripeCustomer ? (
          <>
            <p className={s.blurb}>
              Your card is held by Stripe, our payment processor. {BRAND.name} never sees or
              stores card details.
            </p>
            <div className={s.actions}>
              <Button
                variant="secondary"
                loading={busy === 'createPortalSession'}
                disabled={Boolean(busy)}
                onClick={() => open('createPortalSession')}
              >
                Update Payment Method
              </Button>
            </div>
          </>
        ) : (
          <>
            {/* MMS's words, because they are already the plainest way to say
                it. */}
            <p className={s.blurb}>You haven&rsquo;t added a payment method.</p>
            {/* Ours, not MMS's: what the button below does during the trial
                (#713). Checkout carries the trial's end, so a card added now
                waits for it — see `checkoutTrialEnd` in
                functions/billing/callables.js. */}
            {state === STATES.trial && verdict.trialEndsOn ? (
              <p className={s.note}>
                If you add one now, it isn&rsquo;t charged until your trial ends
                on {formatDate(verdict.trialEndsOn)}.
              </p>
            ) : null}
            <div className={s.actions}>
              <Button
                variant="secondary"
                loading={busy === 'createCheckoutSession'}
                disabled={Boolean(busy) || state === STATES.unknown}
                onClick={() => open('createCheckoutSession')}
              >
                Add Payment Method
              </Button>
            </div>
          </>
        )}
      </div>

      {/* ── Billing History ───────────────────────────────────────────── */}
      <div className={s.section}>
        <div className={s.panelTitle}>Billing History</div>
        {hasBeenBilled ? (
          <>
            <p className={s.blurb}>
              Your invoices and receipts are kept by Stripe, where they can be
              downloaded at any time.
            </p>
            <div className={s.actions}>
              <Button
                variant="secondary"
                loading={busy === 'createPortalSession'}
                disabled={Boolean(busy)}
                onClick={() => open('createPortalSession')}
              >
                View Billing History
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className={s.blurb}>You haven&rsquo;t been billed yet.</p>
            {/* MMS puts this second line beneath the empty state, pointing back
                up at the section above it. Kept, with our own name in it. */}
            <p className={s.note}>
              Add your payment method above to subscribe to {BRAND.name}.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
